import { createFileRoute } from "@tanstack/react-router";
import { useEffect, useRef, useState } from "react";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "sonner";
import {
  MessageCircle,
  Plus,
  Upload,
  X,
  Trash2,
  Pencil,
  FileText,
  Send,
} from "lucide-react";
import { waLink } from "@/lib/whatsapp";

export const Route = createFileRoute("/admin/whatsapp-orders")({
  head: () => ({ meta: [{ title: "WhatsApp Orders — Admin" }] }),
  component: AdminWhatsAppOrders,
});

type WAOrder = {
  id: string;
  customer_name: string;
  phone: string;
  product_details: string;
  notes: string;
  courier: string | null;
  status: string;
  tracking_number: string | null;
  tracking_url: string | null;
  tracking_doc_url: string | null;
  created_at: string;
};

const COURIERS = ["Delhivery", "Bluedart", "DTDC", "India Post", "Ekart", "Shiprocket", "Other"];
const STATUSES = ["pending", "confirmed", "packed", "shipped", "delivered", "cancelled"];

function emptyDraft(): Partial<WAOrder> {
  return {
    customer_name: "",
    phone: "",
    product_details: "",
    notes: "",
    courier: "",
    status: "pending",
    tracking_number: "",
    tracking_url: "",
    tracking_doc_url: "",
  };
}

function AdminWhatsAppOrders() {
  const [orders, setOrders] = useState<WAOrder[]>([]);
  const [search, setSearch] = useState("");
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState<WAOrder | null>(null);
  const [draft, setDraft] = useState<Partial<WAOrder>>(emptyDraft());
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);
  const fileRef = useRef<HTMLInputElement>(null);

  const load = async () => {
    const { data, error } = await supabase
      .from("whatsapp_orders")
      .select("*")
      .order("created_at", { ascending: false });
    if (error) return toast.error(error.message);
    setOrders((data ?? []) as WAOrder[]);
  };

  useEffect(() => {
    load();
  }, []);

  const openNew = () => {
    setEditing(null);
    setDraft(emptyDraft());
    setOpen(true);
  };

  const openEdit = (o: WAOrder) => {
    setEditing(o);
    setDraft({ ...o });
    setOpen(true);
  };

  const handleFile = async (file: File) => {
    if (file.size > 10 * 1024 * 1024) {
      toast.error("File must be under 10MB");
      return;
    }
    setUploading(true);
    const ext = file.name.split(".").pop() || "bin";
    const path = `${crypto.randomUUID()}.${ext}`;
    const { error } = await supabase.storage
      .from("whatsapp-tracking")
      .upload(path, file, { contentType: file.type, cacheControl: "31536000" });
    if (error) {
      setUploading(false);
      return toast.error(error.message);
    }
    setDraft((d) => ({ ...d, tracking_doc_url: path }));
    setUploading(false);
    toast.success("Document uploaded");
  };

  const extractPath = (value: string) => {
    const m = value.match(/whatsapp-tracking\/(.+?)(?:\?|$)/);
    return m ? m[1] : value;
  };

  const openDoc = async (value: string) => {
    const { data, error } = await supabase.storage
      .from("whatsapp-tracking")
      .createSignedUrl(extractPath(value), 3600);
    if (error || !data?.signedUrl) return toast.error("Could not open document");
    window.open(data.signedUrl, "_blank", "noreferrer");
  };

  const signedDocUrl = async (value: string) => {
    const { data } = await supabase.storage
      .from("whatsapp-tracking")
      .createSignedUrl(extractPath(value), 60 * 60 * 24 * 7);
    return data?.signedUrl ?? "";
  };

  const save = async () => {
    if (!draft.customer_name?.trim() || !draft.phone?.trim()) {
      return toast.error("Customer name and phone are required");
    }
    setSaving(true);
    const payload = {
      customer_name: draft.customer_name!.trim(),
      phone: draft.phone!.trim(),
      product_details: draft.product_details ?? "",
      notes: draft.notes ?? "",
      courier: draft.courier || null,
      status: draft.status || "pending",
      tracking_number: draft.tracking_number || null,
      tracking_url: draft.tracking_url || null,
      tracking_doc_url: draft.tracking_doc_url || null,
    };
    const { error } = editing
      ? await supabase.from("whatsapp_orders").update(payload).eq("id", editing.id)
      : await supabase.from("whatsapp_orders").insert(payload);
    setSaving(false);
    if (error) return toast.error(error.message);
    toast.success(editing ? "Order updated" : "Order created");
    setOpen(false);
    load();
  };

  const remove = async (id: string) => {
    if (!confirm("Delete this WhatsApp order?")) return;
    const { error } = await supabase.from("whatsapp_orders").delete().eq("id", id);
    if (error) return toast.error(error.message);
    toast.success("Deleted");
    load();
  };

  const sendMessage = async (o: WAOrder) => {
    const lines = [
      `Hi ${o.customer_name.split(" ")[0]}, here's the tracking info for your order:`,
      `Status: ${o.status}`,
    ];
    if (o.courier) lines.push(`Courier: ${o.courier}`);
    if (o.tracking_number) lines.push(`Tracking #: ${o.tracking_number}`);
    if (o.tracking_url) lines.push(`Track: ${o.tracking_url}`);
    if (o.tracking_doc_url) {
      const signed = await signedDocUrl(o.tracking_doc_url);
      if (signed) lines.push(`Document: ${signed}`);
    }
    window.open(waLink(o.phone, lines.join("\n")), "_blank");
  };

  const filtered = orders.filter((o) => {
    const q = search.toLowerCase();
    return (
      !q ||
      o.customer_name.toLowerCase().includes(q) ||
      o.phone.includes(q) ||
      (o.tracking_number ?? "").toLowerCase().includes(q)
    );
  });

  return (
    <div>
      <div className="flex flex-wrap items-center justify-between gap-3 mb-6">
        <div>
          <h2 className="display text-2xl">WhatsApp Orders</h2>
          <p className="text-sm text-muted-foreground">
            Manual orders received via WhatsApp with tracking ID document upload.
          </p>
        </div>
        <button
          onClick={openNew}
          className="inline-flex items-center gap-2 rounded-md bg-foreground text-background px-4 py-2 text-sm hover:opacity-90"
        >
          <Plus className="h-4 w-4" /> New order
        </button>
      </div>

      <input
        value={search}
        onChange={(e) => setSearch(e.target.value)}
        placeholder="Search by name, phone, tracking #"
        className="w-full mb-4 rounded-md border border-border bg-background px-3 py-2 text-sm"
      />

      <div className="space-y-3">
        {filtered.length === 0 && (
          <p className="text-sm text-muted-foreground py-8 text-center border border-dashed border-border rounded-md">
            No WhatsApp orders yet.
          </p>
        )}
        {filtered.map((o) => (
          <div
            key={o.id}
            className="border border-border rounded-md p-4 flex flex-wrap gap-4 items-start justify-between"
          >
            <div className="min-w-0 flex-1">
              <div className="flex items-center gap-2 flex-wrap">
                <span className="font-medium">{o.customer_name}</span>
                <span className="text-xs text-muted-foreground">{o.phone}</span>
                <span className="text-xs rounded-full bg-muted px-2 py-0.5">{o.status}</span>
              </div>
              {o.product_details && (
                <p className="text-sm text-muted-foreground mt-1 line-clamp-2">{o.product_details}</p>
              )}
              <div className="text-xs text-muted-foreground mt-2 flex gap-3 flex-wrap">
                {o.courier && <span>Courier: {o.courier}</span>}
                {o.tracking_number && <span>#{o.tracking_number}</span>}
                {o.tracking_doc_url && (
                  <button
                    type="button"
                    onClick={() => openDoc(o.tracking_doc_url!)}
                    className="inline-flex items-center gap-1 text-foreground underline"
                  >
                    <FileText className="h-3 w-3" /> document
                  </button>
                )}
              </div>
            </div>
            <div className="flex gap-2 shrink-0">
              <button
                onClick={() => sendMessage(o)}
                className="inline-flex items-center gap-1 rounded-md px-3 py-1.5 text-xs text-white"
                style={{ backgroundColor: "#25D366" }}
              >
                <Send className="h-3 w-3" /> WhatsApp
              </button>
              <button
                onClick={() => openEdit(o)}
                className="inline-flex items-center gap-1 rounded-md border border-border px-3 py-1.5 text-xs"
              >
                <Pencil className="h-3 w-3" /> Edit
              </button>
              <button
                onClick={() => remove(o.id)}
                className="inline-flex items-center gap-1 rounded-md border border-border px-3 py-1.5 text-xs text-destructive"
              >
                <Trash2 className="h-3 w-3" />
              </button>
            </div>
          </div>
        ))}
      </div>

      {open && (
        <div className="fixed inset-0 z-50 bg-black/40 flex items-start justify-center p-4 overflow-y-auto">
          <div className="bg-background border border-border rounded-lg w-full max-w-2xl my-8 p-6">
            <div className="flex items-center justify-between mb-4">
              <h3 className="display text-xl flex items-center gap-2">
                <MessageCircle className="h-5 w-5" />
                {editing ? "Edit WhatsApp order" : "New WhatsApp order"}
              </h3>
              <button onClick={() => setOpen(false)} aria-label="Close">
                <X className="h-5 w-5" />
              </button>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <Field label="Customer name *">
                <input
                  value={draft.customer_name ?? ""}
                  onChange={(e) => setDraft({ ...draft, customer_name: e.target.value })}
                  className="input"
                />
              </Field>
              <Field label="Phone *">
                <input
                  value={draft.phone ?? ""}
                  onChange={(e) => setDraft({ ...draft, phone: e.target.value })}
                  placeholder="+91..."
                  className="input"
                />
              </Field>
              <Field label="Product / order details" full>
                <textarea
                  rows={3}
                  value={draft.product_details ?? ""}
                  onChange={(e) => setDraft({ ...draft, product_details: e.target.value })}
                  className="input"
                />
              </Field>
              <Field label="Notes" full>
                <textarea
                  rows={2}
                  value={draft.notes ?? ""}
                  onChange={(e) => setDraft({ ...draft, notes: e.target.value })}
                  className="input"
                />
              </Field>
              <Field label="Courier">
                <select
                  value={draft.courier ?? ""}
                  onChange={(e) => setDraft({ ...draft, courier: e.target.value })}
                  className="input"
                >
                  <option value="">Select courier</option>
                  {COURIERS.map((c) => (
                    <option key={c} value={c}>
                      {c}
                    </option>
                  ))}
                </select>
              </Field>
              <Field label="Status">
                <select
                  value={draft.status ?? "pending"}
                  onChange={(e) => setDraft({ ...draft, status: e.target.value })}
                  className="input"
                >
                  {STATUSES.map((s) => (
                    <option key={s} value={s}>
                      {s}
                    </option>
                  ))}
                </select>
              </Field>
              <Field label="Tracking number" full>
                <input
                  value={draft.tracking_number ?? ""}
                  onChange={(e) => setDraft({ ...draft, tracking_number: e.target.value })}
                  className="input"
                />
              </Field>
              <Field label="Tracking URL" full>
                <input
                  value={draft.tracking_url ?? ""}
                  onChange={(e) => setDraft({ ...draft, tracking_url: e.target.value })}
                  placeholder="https://..."
                  className="input"
                />
              </Field>
            </div>

            {draft.customer_name && draft.phone && (
              <div className="mt-5 rounded-md border border-border p-4">
                <p className="font-medium">Send tracking to customer</p>
                <p className="text-xs text-muted-foreground mb-2">
                  Opens WhatsApp with a pre-filled message to the customer.
                </p>
                <button
                  onClick={() =>
                    sendMessage({ ...(emptyDraft() as WAOrder), ...(draft as WAOrder) })
                  }
                  className="inline-flex items-center gap-2 rounded-md px-4 py-2 text-sm text-white"
                  style={{ backgroundColor: "#25D366" }}
                >
                  <Send className="h-4 w-4" /> Send WhatsApp message
                </button>
              </div>
            )}

            <div className="mt-5">
              <p className="font-medium">Tracking ID document</p>
              <p className="text-xs text-muted-foreground mb-2">
                Upload screenshot, label, or PDF (max 10MB).
              </p>
              {draft.tracking_doc_url ? (
                <div className="flex items-center gap-3 border border-border rounded-md p-3">
                  <FileText className="h-4 w-4" />
                  <button
                    type="button"
                    onClick={() => openDoc(draft.tracking_doc_url!)}
                    className="text-sm underline truncate flex-1 text-left"
                  >
                    View document
                  </button>
                  <button
                    onClick={() => setDraft({ ...draft, tracking_doc_url: "" })}
                    className="text-xs text-destructive"
                  >
                    Remove
                  </button>
                </div>
              ) : (
                <button
                  type="button"
                  onClick={() => fileRef.current?.click()}
                  disabled={uploading}
                  className="w-full border border-dashed border-border rounded-md py-6 text-sm inline-flex items-center justify-center gap-2 hover:bg-muted/40 disabled:opacity-60"
                >
                  <Upload className="h-4 w-4" />
                  {uploading ? "Uploading…" : "Upload document"}
                </button>
              )}
              <input
                ref={fileRef}
                type="file"
                accept="image/*,application/pdf"
                className="hidden"
                onChange={(e) => {
                  const f = e.target.files?.[0];
                  if (f) handleFile(f);
                  e.target.value = "";
                }}
              />
            </div>

            <div className="mt-6 flex justify-end gap-2">
              <button
                onClick={() => setOpen(false)}
                className="rounded-md border border-border px-4 py-2 text-sm"
              >
                Cancel
              </button>
              <button
                onClick={save}
                disabled={saving}
                className="rounded-md bg-foreground text-background px-4 py-2 text-sm disabled:opacity-60"
              >
                {saving ? "Saving…" : "Save"}
              </button>
            </div>
          </div>
        </div>
      )}

      <style>{`.input{width:100%;border:1px solid hsl(var(--border));background:hsl(var(--background));border-radius:0.375rem;padding:0.5rem 0.75rem;font-size:0.875rem;}`}</style>
    </div>
  );
}

function Field({
  label,
  children,
  full,
}: {
  label: string;
  children: React.ReactNode;
  full?: boolean;
}) {
  return (
    <label className={`block ${full ? "sm:col-span-2" : ""}`}>
      <span className="text-sm font-medium block mb-1">{label}</span>
      {children}
    </label>
  );
}
